import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import localforage from "localforage";

export interface WidgetConfig {
  id: string;
  title: string;
  component: string;
  visible: boolean;
  layout: {
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    minH?: number;
    maxW?: number;
    maxH?: number;
  };
}

export interface DashboardLayout {
  widgets: WidgetConfig[];
  cols: number;
  rowHeight: number;
}

interface DashboardLayoutContextType {
  layout: DashboardLayout;
  updateLayout: (newLayout: WidgetConfig[]) => void;
  toggleWidget: (widgetId: string) => void;
  resetLayout: () => void;
  saveLayout: () => Promise<void>;
  isCustomizing: boolean;
  setIsCustomizing: (customizing: boolean) => void;
}

const defaultLayout: DashboardLayout = {
  cols: 12,
  rowHeight: 120,
  widgets: [
    {
      id: "dashboard-metrics",
      title: "Dashboard Overview",
      component: "DashboardMetrics",
      visible: true,
      layout: { x: 0, y: 0, w: 12, h: 3, minW: 8, minH: 3 },
    },
    {
      id: "overview-metrics",
      title: "Overview Metrics",
      component: "OverviewMetrics",
      visible: true,
      layout: { x: 0, y: 3, w: 12, h: 2, minW: 6, minH: 2 },
    },
    {
      id: "traffic-summary",
      title: "Traffic Sources Summary",
      component: "TrafficSummary",
      visible: true,
      layout: { x: 0, y: 5, w: 12, h: 2, minW: 6, minH: 1 },
    },
    {
      id: "top-source-categories",
      title: "Top Performing Source Categories",
      component: "TopSourceCategories",
      visible: true,
      layout: { x: 0, y: 7, w: 12, h: 6, minW: 6, minH: 4 },
    },
    {
      id: "top-links",
      title: "Top Performing Links",
      component: "TopLinks",
      visible: true,
      layout: { x: 0, y: 11, w: 6, h: 4, minW: 4, minH: 3 },
    },
    {
      id: "top-locations",
      title: "Top Performing Locations",
      component: "TopLocations",
      visible: true,
      layout: { x: 6, y: 11, w: 6, h: 4, minW: 4, minH: 3 },
    },
    {
      id: "device-analytics-table",
      title: "Device Analytics Table",
      component: "DeviceAnalyticsTable",
      visible: true,
      layout: { x: 0, y: 15, w: 12, h: 5, minW: 8, minH: 4 },
    },
    {
      id: "real-time-activity",
      title: "Real-Time Activity",
      component: "RealTimeActivity",
      visible: true,
      layout: { x: 0, y: 20, w: 6, h: 4, minW: 4, minH: 3 },
    },
    {
      id: "popular-pages",
      title: "Popular Pages",
      component: "PopularPages",
      visible: true,
      layout: { x: 6, y: 20, w: 6, h: 4, minW: 4, minH: 3 },
    },
    {
      id: "traffic-sources-chart",
      title: "Traffic Sources Chart",
      component: "TrafficSourcesChart",
      visible: true,
      layout: { x: 0, y: 24, w: 6, h: 4, minW: 4, minH: 3 },
    },
    {
      id: "location-chart",
      title: "Location Analytics",
      component: "LocationChart",
      visible: true,
      layout: { x: 6, y: 24, w: 6, h: 4, minW: 4, minH: 3 },
    },
    {
      id: "search-keywords",
      title: "Search Keywords",
      component: "SearchKeywords",
      visible: true,
      layout: { x: 0, y: 28, w: 4, h: 3, minW: 3, minH: 2 },
    },
    {
      id: "user-journeys",
      title: "User Journeys",
      component: "UserJourneys",
      visible: true,
      layout: { x: 4, y: 28, w: 4, h: 3, minW: 3, minH: 2 },
    },
    {
      id: "system-info",
      title: "System Info",
      component: "SystemInfo",
      visible: true,
      layout: { x: 8, y: 28, w: 4, h: 3, minW: 3, minH: 2 },
    },
    {
      id: "performance-metrics",
      title: "Performance Metrics",
      component: "PerformanceMetrics",
      visible: false,
      layout: { x: 0, y: 32, w: 6, h: 5, minW: 6, minH: 4 },
    },
    {
      id: "peak-hours",
      title: "Peak Hours Analysis",
      component: "PeakHours",
      visible: false,
      layout: { x: 6, y: 32, w: 6, h: 5, minW: 6, minH: 4 },
    },
    {
      id: "conversion-funnel",
      title: "Conversion Funnel",
      component: "ConversionFunnel",
      visible: false,
      layout: { x: 0, y: 38, w: 12, h: 6, minW: 8, minH: 5 },
    },
  ],
};

const DashboardLayoutContext = createContext<
  DashboardLayoutContextType | undefined
>(undefined);

export const useDashboardLayout = () => {
  const context = useContext(DashboardLayoutContext);
  if (!context) {
    throw new Error(
      "useDashboardLayout must be used within a DashboardLayoutProvider",
    );
  }
  return context;
};

interface DashboardLayoutProviderProps {
  children: ReactNode;
}

export const DashboardLayoutProvider: React.FC<
  DashboardLayoutProviderProps
> = ({ children }) => {
  const [layout, setLayout] = useState<DashboardLayout>(defaultLayout);
  const [isCustomizing, setIsCustomizing] = useState(false);

  // Load saved layout on mount
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const savedLayout =
          await localforage.getItem<DashboardLayout>("dashboard-layout");
        if (savedLayout) {
          setLayout(savedLayout);
        }
      } catch (error) {
        console.error("Failed to load dashboard layout:", error);
      }
    };

    loadLayout();
  }, []);

  const updateLayout = (newWidgets: WidgetConfig[]) => {
    const newLayout = {
      ...layout,
      widgets: newWidgets,
    };
    setLayout(newLayout);
  };

  const toggleWidget = (widgetId: string) => {
    const newWidgets = layout.widgets.map((widget) =>
      widget.id === widgetId ? { ...widget, visible: !widget.visible } : widget,
    );
    updateLayout(newWidgets);
  };

  const resetLayout = () => {
    setLayout(defaultLayout);
    localforage.removeItem("dashboard-layout");
  };

  const saveLayout = async () => {
    try {
      await localforage.setItem("dashboard-layout", layout);
    } catch (error) {
      console.error("Failed to save dashboard layout:", error);
    }
  };

  // Auto-save layout changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveLayout();
    }, 1000); // Debounce saves by 1 second

    return () => clearTimeout(timeoutId);
  }, [layout]);

  const value: DashboardLayoutContextType = {
    layout,
    updateLayout,
    toggleWidget,
    resetLayout,
    saveLayout,
    isCustomizing,
    setIsCustomizing,
  };

  return (
    <DashboardLayoutContext.Provider value={value}>
      {children}
    </DashboardLayoutContext.Provider>
  );
};
